// Mirrored from mju-ai-workbench/shared/schemas/remoteControl.ts (e752c191, D282, D372, the board's pause and D414) — keep the two identical but for this line and the import extensions.
/**
 * Remote control of a live session from the web and the phone (D282, plan
 * `docs/plans/2026-10-remote-control.md`). Owned by the workbench: the AIOE
 * console and phone app mirror these shapes, they never define them.
 *
 *  - {@link remoteSessionSchema}: one session a person has opened to remote
 *    control, as a machine reports it — on the fleet heartbeat
 *    (`remoteSessions`, at most {@link MAX_REMOTE_SESSIONS}) so the console
 *    can list it before it reaches the machine, and on `sessions.list`'s
 *    `remote` for the live detail.
 *  - {@link LiveEvent}: what `sessions.subscribe` streams, in order.
 *  - The resume token (`after`): opaque to a client, which echoes it
 *    (made and read by src/control/liveSession.ts).
 *  - D372 (agreed with AIOE on 27 Oct 2026): the stream's structure — a
 *    `turn` per message (system lines, markers, questions, blocks), approvals
 *    with what they would do, tools with their words, the member a delta is
 *    from, the usage limit, a finished turn's files and the task strip's
 *    people. All optional: a console that reads none of them is unaffected.
 *
 * Words for people: session, conversation, the web, the phone; never an id.
 */
import { z } from "zod";
/** Where a remote action came from: the console in a browser, or the phone app. */
export const REMOTE_VIA = ["phone", "web"];
/** At most this many sessions on a heartbeat. */
export const MAX_REMOTE_SESSIONS = 50;
/** What kind of session it is, in the console's words: Chat, Research, Team Work, or an agent of its own. */
export const REMOTE_SESSION_MODES = ["chat", "research", "teamwork", "agent"];
/** One session open to remote control, as a machine reports it. */
export const remoteSessionSchema = z.object({
    /** The agent session, for an agent of its own (a Mission Control session). */
    sessionId: z.string().min(1).max(200).optional(),
    /** The Chat, Research or Team Work conversation: what a client names for those. */
    conversationId: z.string().min(1).max(200).optional(),
    /** The repository it runs in (the project's root id), when the machine knows it. */
    rootId: z.string().min(1).max(200).optional(),
    mode: z.enum(REMOTE_SESSION_MODES),
    /** The session's title, as the desktop shows it. */
    title: z.string().max(200),
    /** When remote control was turned on for it (ISO). */
    since: z.string().max(40),
    /** The person it runs for (their principal): the only person who may control it. */
    person: z.string().max(200).optional(),
    /** Being controlled right now: by whom (a first name) and from where, and when they last acted (ISO). */
    controlledBy: z.object({ name: z.string().max(120), via: z.enum(REMOTE_VIA), at: z.string().max(40) }).optional(),
    /** What it is doing: thinking, waiting for you, done, stopped. */
    status: z.enum(["thinking", "waiting", "done", "stopped", "idle"]).optional(),
});
export const remoteSessionsReportSchema = z.array(remoteSessionSchema).max(MAX_REMOTE_SESSIONS);
export { controllerWords, LIVE_STATUS_WORDS } from "../wording/remoteWords.js";
// ── The stream's structure (D372) ─────────────────────────────────────
//
// Agreed with AIOE on 27 Oct 2026. Every field below that D282 did not have is
// optional, so a console that reads none of them sees what it saw before.
/** A turn's text at most, as before. */
export const LIVE_TURN_TEXT_MAX = 20_000;
/** An approval's diff at most, cut at a line. */
export const LIVE_DIFF_MAX = 20_000;
/** A finished turn's changed files at most. */
export const LIVE_CHANGED_FILES_MAX = 200;
/** D414: a usage limit's own words from the backend at most, masked. */
export const LIVE_LIMIT_DETAIL_MAX = 2000;
/** D414: a turn's ending checkpoint, as `changes.checkpoint` names it and `git.diff { checkpoint }` takes it. */
export const LIVE_CHECKPOINT_PATTERN = /^[0-9a-f]{7,64}$/;
//# sourceMappingURL=remoteControl.js.map